import { Category } from '../types/Category';
import { Operation, OperationType } from '../types/History';
import { findCategoryInTree } from '../utils/CategoryUtils';

export type HistoryReplayCategoryNames = {
  sourceCategoryName?: string;
  targetCategoryName?: string;
};

/** Source/target columns for undo/redo activity rows (best-effort from history payload). */
export function historyReplayCategoryNames(
  operation: Operation,
  categoryTree: Category[],
): HistoryReplayCategoryNames {
  const resolveId = (id: string | null | undefined): string | undefined => {
    if (!id) return undefined;
    const name = findCategoryInTree(categoryTree, id)?.name?.trim();
    return name || undefined;
  };
  const catName = (cat: { name?: string } | null | undefined): string | undefined => {
    const name = cat?.name?.trim();
    return name || undefined;
  };
  const parentNameFrom = (d: {
    parentName?: string | null;
    parentId?: string | null;
  }): string | undefined => d.parentName?.trim() || resolveId(d.parentId);

  const items: unknown[] = Array.isArray(operation.data)
    ? operation.data
    : operation.data != null
      ? [operation.data]
      : [];
  const d = items[0] as Record<string, unknown> | undefined;
  if (!d) return {};

  switch (operation.type) {
    case 'deleteCategory':
    case 'addCategory': {
      const category = d.category as Category | undefined;
      return {
        sourceCategoryName: catName(category),
        targetCategoryName: parentNameFrom(d as { parentId?: string | null; parentName?: string | null }),
      };
    }
    case 'moveCategory':
    case 'dropCategory':
    case 'dragCategoryTo':
    case 'pasteCategory':
    case 'pasteCategoriesIntoBranch': {
      const category = d.category as Category | undefined;
      return {
        sourceCategoryName: resolveId(d.oldParentId as string | undefined) ?? catName(category),
        targetCategoryName: resolveId(d.newParentId as string | undefined)
          ?? resolveId(d.parentId as string | undefined),
      };
    }
    case 'moveCategoryUp':
    case 'moveCategoryDown':
    case 'moveCategoryTo': {
      const category = d.category as Category | undefined;
      const parentId = (d.parentId as string | undefined) ?? category?.parentId;
      return {
        sourceCategoryName: catName(category),
        targetCategoryName: resolveId(parentId),
      };
    }
    case 'deleteContent': {
      return {
        sourceCategoryName: resolveId(d.categoryId as string | undefined)
          ?? resolveId((d.content as { parentId?: string } | undefined)?.parentId),
      };
    }
    case 'addContent':
    case 'updateContent':
    case 'pasteContent': {
      return {
        targetCategoryName: resolveId(d.categoryId as string | undefined)
          ?? resolveId(d.parentId as string | undefined),
      };
    }
    case 'moveContent':
    case 'dropContent': {
      const content = d.content as { subcase?: string } | undefined;
      void content;
      return {
        sourceCategoryName: resolveId(d.oldParentId as string | undefined),
        targetCategoryName: resolveId(d.newParentId as string | undefined)
          ?? resolveId(d.categoryId as string | undefined),
      };
    }
    case 'moveContentUp':
    case 'moveContentDown':
    case 'dragContentTo': {
      return {
        targetCategoryName: resolveId(d.categoryId as string | undefined)
          ?? resolveId(d.parentId as string | undefined),
      };
    }
    case 'renameCategory': {
      const category = d.category as Category | undefined;
      return {
        targetCategoryName: catName(category) ?? (d.oldName as string | undefined)?.trim(),
      };
    }
    case 'splitCategory': {
      const original = d.originalCategory as Category | undefined;
      const parentId = d.parentId as string | undefined;
      return {
        sourceCategoryName: catName(original),
        targetCategoryName: resolveId(parentId) ?? catName(original),
      };
    }
    case 'mergeCategories':
    case 'groupCategories':
    case 'absorbIntoContentCategory':
    case 'flattenCategory':
    case 'collapseSubtreeToContentCategory':
    case 'addMatchingContent': {
      const category = d.category as Category | undefined;
      return {
        sourceCategoryName: catName(category),
        targetCategoryName: resolveId(d.parentId as string | undefined)
          ?? resolveId(d.targetContentCategoryId as string | undefined),
      };
    }
    default:
      return {};
  }
}

interface DocumentHistory {
  undoStack: Operation[];
  redoStack: Operation[];
}

/** Collect content ids from deleteContent operations (undo stack eviction / redo discard). */
export function collectContentIdsFromDeleteOperations(operations: Operation[]): string[] {
  const out = new Set<string>();
  for (const op of operations) {
    if (op.type === 'deleteContent') {
      const arr = Array.isArray(op.data) ? op.data : [op.data];
      for (const item of arr) {
        const id = item?.content?.id as string | undefined;
        if (id) out.add(id);
      }
    } else if (op.type === 'treeAssistantBatch') {
      const pack = op.data as { redoSteps?: unknown[]; undoSteps?: unknown[] } | undefined;
      const steps = pack?.redoSteps ?? [];
      let hasBulkCategoryDelete = false;
      for (const raw of steps) {
        const s = raw as Record<string, unknown>;
        if (s?.type === 'DELETE_CONTENT' && typeof s.contentId === 'string') {
          out.add(s.contentId);
        }
        if (s?.type === 'DELETE_ALL_CONTENT_IN_CATEGORY') {
          hasBulkCategoryDelete = true;
        }
      }
      if (hasBulkCategoryDelete) {
        const undo = pack?.undoSteps ?? [];
        for (const raw of undo) {
          const u = raw as Record<string, unknown>;
          if (u?.type === 'RESTORE_CONTENT' && typeof u.contentId === 'string') {
            out.add(u.contentId);
          }
        }
      }
    }
  }
  return Array.from(out);
}

/** Human-readable label for Undo/Redo tooltips (from forward redo steps). */
export function buildTreeAssistantBatchTooltip(history: { undoSteps?: unknown[]; redoSteps?: unknown[] }): string {
  const steps = history.redoSteps ?? [];
  const counts = new Map<string, number>();
  for (const raw of steps) {
    if (!raw || typeof raw !== 'object') continue;
    const t = (raw as Record<string, unknown>).type;
    if (typeof t === 'string') {
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
  }
  const phrases: string[] = [];
  const del = counts.get('DELETE_CONTENT') ?? 0;
  if (del === 1) phrases.push('delete content');
  else if (del > 1) phrases.push(`delete ${del} content rows`);
  const add = counts.get('RESTORE_CONTENT') ?? 0;
  if (add === 1) phrases.push('add content');
  else if (add > 1) phrases.push(`add ${add} content rows`);
  const addCat = counts.get('ADD_CATEGORY') ?? 0;
  if (addCat === 1) phrases.push('add category');
  else if (addCat > 1) phrases.push(`add ${addCat} categories`);
  const delCat =
    (counts.get('DELETE_CATEGORY') ?? 0) + (counts.get('DELETE_CATEGORY_FOR_REDO') ?? 0);
  if (delCat === 1) phrases.push('remove category');
  else if (delCat > 1) phrases.push(`remove ${delCat} categories`);
  const mvCat = counts.get('MOVE_CATEGORY') ?? 0;
  if (mvCat === 1) phrases.push('move category');
  else if (mvCat > 1) phrases.push(`move ${mvCat} categories`);
  const cpCat = counts.get('COPY_CATEGORY') ?? 0;
  if (cpCat === 1) phrases.push('copy category');
  else if (cpCat > 1) phrases.push(`copy ${cpCat} categories`);
  const mvCo = counts.get('MOVE_CONTENT') ?? 0;
  if (mvCo === 1) phrases.push('move content');
  else if (mvCo > 1) phrases.push(`move ${mvCo} content rows`);
  const cpCo = counts.get('COPY_CONTENT') ?? 0;
  if (cpCo === 1) phrases.push('copy content');
  else if (cpCo > 1) phrases.push(`copy ${cpCo} content rows`);
  const rnCat = counts.get('RENAME_CATEGORY') ?? 0;
  if (rnCat === 1) phrases.push('rename category');
  else if (rnCat > 1) phrases.push(`rename ${rnCat} categories`);
  const upDesc = counts.get('UPDATE_CATEGORY_DESCRIPTION') ?? 0;
  if (upDesc === 1) phrases.push('update description');
  else if (upDesc > 1) phrases.push(`update ${upDesc} descriptions`);
  const rnSub = counts.get('RENAME_SUBCASE') ?? 0;
  if (rnSub === 1) phrases.push('rename subcase');
  else if (rnSub > 1) phrases.push(`rename ${rnSub} subcases`);
  const split = counts.get('SPLIT_CATEGORY') ?? 0;
  if (split === 1) phrases.push('split category');
  else if (split > 1) phrases.push(`split ${split} categories`);
  const org1 = counts.get('ORGANIZE_CONTENT_CATEGORY_ONE_LAYER') ?? 0;
  if (org1 === 1) phrases.push('organize content (one layer)');
  else if (org1 > 1) phrases.push(`organize content (${org1}×)`);
  const delAll = counts.get('DELETE_ALL_CONTENT_IN_CATEGORY') ?? 0;
  if (delAll === 1) phrases.push('clear category content');
  else if (delAll > 1) phrases.push(`clear category content (${delAll}×)`);
  const outline = counts.get('APPLY_CATEGORY_OUTLINE') ?? 0;
  if (outline === 1) phrases.push('apply category outline');
  else if (outline > 1) phrases.push(`apply category outline (${outline}×)`);

  if (phrases.length > 0) {
    return `Structure builder — ${phrases.join(', ')}`;
  }
  const n = steps.length;
  if (n === 1) return 'Structure builder (1 step)';
  if (n > 1) return `Structure builder (${n} steps)`;
  return 'Structure builder';
}

export class HistoryService {
  private documentHistories: Map<string, DocumentHistory> = new Map();
  private isRecording = true;
  private static instance: HistoryService;
  private currentDocumentId: string | null = null;

  /** Oldest undo steps are dropped when the stack grows past this size. */
  public static readonly MAX_UNDO_STACK = 50;

  private deferredDeleteMediaListener: ((operations: Operation[]) => void) | null = null;

  private constructor() {}

  public static getInstance(): HistoryService {
    if (!HistoryService.instance) {
      HistoryService.instance = new HistoryService();
    }
    return HistoryService.instance;
  }

  /**
   * Invoked when deleteContent operations may need on-disk media cleanup:
   * — bottom of undo stack evicted (cannot reach that state by undo anymore)
   * — redo stack discarded (superseded by a new edit)
   * — document history cleared (document closed / deleted from app)
   */
  setDeferredDeleteMediaListener(listener: ((operations: Operation[]) => void) | null) {
    this.deferredDeleteMediaListener = listener;
  }

  private emitDeferredDeleteMedia(operations: Operation[]) {
    if (operations.length === 0 || !this.deferredDeleteMediaListener) return;
    this.deferredDeleteMediaListener(operations);
  }

  private evictUndoStackBottomIfNeeded() {
    if (!this.currentDocumentId) return;
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return;
    const evicted: Operation[] = [];
    while (history.undoStack.length > HistoryService.MAX_UNDO_STACK) {
      evicted.push(history.undoStack.shift()!);
    }
    this.emitDeferredDeleteMedia(evicted);
  }

  setCurrentDocument(documentId: string | null) {
    this.currentDocumentId = documentId;
    if (documentId && !this.documentHistories.has(documentId)) {
      this.documentHistories.set(documentId, {
        undoStack: [],
        redoStack: [],
      });
    }
  }

  startRecording() {
    this.isRecording = true;
  }

  stopRecording() {
    this.isRecording = false;
  }

  private getOperationName(type: string): string {
    switch (type) {
      case 'splitCategory':
        return 'Split Category';
      case 'groupCategories':
        return 'Group categories';
      case 'absorbIntoContentCategory':
        return 'Absorb categories into content category';
      case 'flattenCategory':
        return 'Flatten category';
      case 'collapseSubtreeToContentCategory':
        return 'Collect branch content';
      case 'collapseSubtreeToContentCategoryBulk':
        return 'Collect branch content (multiple)';
      case 'addMatchingContent':
        return 'Add matching content (AI)';
      case 'mergeCategories':
        return 'Merge categories';
      case 'deleteCategory':
        return 'Delete Category';
      case 'addCategory':
        return 'Add Category';
      case 'renameCategory':
        return 'Rename Category';
      case 'deleteContent':
        return 'Delete Content';
      case 'addContent':
        return 'Add Content';
      case 'updateContent':
        return 'Update Content';
      case 'pasteCategory':
        return 'Paste Category';
      case 'pasteCategoriesIntoBranch':
        return 'Paste categories into branch';
      case 'dropCategory':
        return 'Drag and Drop Category';
      case 'moveCategoryTo':
        return 'Drag and Drop Category';
      case 'dragCategoryTo':
        return 'Drag Categories Up or Down';
      case 'pasteContent':
        return 'Paste Content';
      case 'moveCategoryUp':
        return 'Move Category Up';
      case 'moveCategoryDown':
        return 'Move Category Down';
      case 'moveContentUp':
        return 'Move Content Up';
      case 'moveContentDown':
        return 'Move Content Down';
      case 'dragContentTo':
        return 'Drag Content Up or Down';
      case 'dropContent':
        return 'Drag and Drop Content';
      case 'renameSubcase':
        return 'Rename Subcase';
      case 'generateSubtree':
        return 'Structure categories (AI)';
      case 'generateInitialCategories':
        return 'Structure categories (AI) — initial';
      case 'proposeParentCategories':
        return 'Structure categories (AI) — parent layers';
      case 'treeAssistantBatch':
        return 'Structure builder';
      default:
        return type;
    }
  }

  recordOperation(operation: Operation) {
    if (!this.isRecording || !this.currentDocumentId) return;

    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return;

    const discardedRedo = [...history.redoStack];
    history.redoStack = [];
    this.emitDeferredDeleteMedia(discardedRedo);

    // If the operation is part of a batch, add it to the last operation.
    // Do not merge these types: `isBatch` there means cut-vs-copy / multi-item semantics on a *single*
    // user action, not "merge with the next gesture". Otherwise two separate cut-pastes or two moves
    // would become one undo step (same bug as consecutive deletes used to have).
    const noMergeIntoPrevious: OperationType[] = ['deleteContent', 'deleteCategory', 'pasteContent', 'dropContent', 'proposeParentCategories'];
    if (
      history.undoStack.length > 0 &&
      !noMergeIntoPrevious.includes(operation.type) &&
      history.undoStack[history.undoStack.length - 1].type === operation.type &&
      history.undoStack[history.undoStack.length - 1].isBatch
    ) {
      const lastOp = history.undoStack[history.undoStack.length - 1];
      if (Array.isArray(lastOp.data)) {
        lastOp.data.push(...(Array.isArray(operation.data) ? operation.data : [operation.data]));
      }
    } else {
      history.undoStack.push(operation);
    }
    this.evictUndoStackBottomIfNeeded();
  }

  startBatchOperation(type: OperationType) {
    if (!this.currentDocumentId) return;
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return;

    history.undoStack.push({
      type,
      data: [],
      isBatch: true,
    });
    this.evictUndoStackBottomIfNeeded();
  }

  endBatchOperation() {
    // No action needed, the batch is already recorded
  }

  undo(): Operation | null {
    if (!this.currentDocumentId) return null;
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return null;

    const operation = history.undoStack.pop();
    if (operation) {
      history.redoStack.push(operation);
      return operation;
    }
    return null;
  }

  redo(): Operation | null {
    if (!this.currentDocumentId) return null;
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return null;

    const operation = history.redoStack.pop();
    if (operation) {
      history.undoStack.push(operation);
      return operation;
    }
    return null;
  }

  canUndo(): boolean {
    if (!this.currentDocumentId) return false;
    const history = this.documentHistories.get(this.currentDocumentId);
    return history ? history.undoStack.length > 0 : false;
  }

  canRedo(): boolean {
    if (!this.currentDocumentId) return false;
    const history = this.documentHistories.get(this.currentDocumentId);
    return history ? history.redoStack.length > 0 : false;
  }

  clear() {
    this.documentHistories.clear();
    this.currentDocumentId = null;
  }

  clearDocumentHistory(documentId: string) {
    const history = this.documentHistories.get(documentId);
    if (history) {
      const all = [...history.undoStack, ...history.redoStack];
      this.emitDeferredDeleteMedia(all);
    }
    this.documentHistories.delete(documentId);
    if (this.currentDocumentId === documentId) {
      this.currentDocumentId = null;
    }
  }

  /** Move undo/redo stacks when a document library name (tab id) changes; no-op if no stack under {@code fromId}. */
  rekeyDocumentHistory(fromId: string, toId: string) {
    if (!fromId || !toId || fromId === toId) return;
    const history = this.documentHistories.get(fromId);
    if (history) {
      this.documentHistories.delete(fromId);
      this.documentHistories.set(toId, history);
    }
    if (this.currentDocumentId === fromId) {
      this.currentDocumentId = toId;
    }
  }

  getOperationDisplayName(operation: Operation): string {
    if (typeof operation.label === 'string' && operation.label.trim()) {
      return operation.label.trim();
    }
    if (
      operation.type === 'treeAssistantBatch'
      && operation.data
      && typeof (operation.data as { tooltipLabel?: unknown }).tooltipLabel === 'string'
    ) {
      const label = String((operation.data as { tooltipLabel: string }).tooltipLabel).trim();
      if (label) return label;
    }
    return this.getOperationName(operation.type);
  }

  getUndoOperationName(): string {
    if (!this.currentDocumentId) return '';
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history || history.undoStack.length === 0) return '';
    const op = history.undoStack[history.undoStack.length - 1];
    return this.getOperationDisplayName(op);
  }

  getRedoOperationName(): string {
    if (!this.currentDocumentId) return '';
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history || history.redoStack.length === 0) return '';
    const op = history.redoStack[history.redoStack.length - 1];
    return this.getOperationDisplayName(op);
  }
}

export const historyService = HistoryService.getInstance();
